"use client";

import { useCallback } from "react";
import { X } from "lucide-react";
import { domId } from "@/lib/dom-id";

export interface CustomHeader {
  id: string;
  name: string;
  value: string;
  // Custom-header values are credentials by default. The API redacts stored
  // values on reads; an empty secret value means "preserve the stored value".
  is_secret: boolean;
  // UI-only marker derived from a redacted API response.
  has_stored_value?: boolean;
}

export function emptyCustomHeader(): CustomHeader {
  return {
    id:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : String(Math.random()),
    name: "",
    value: "",
    is_secret: true,
    has_stored_value: false,
  };
}

interface Props {
  value: CustomHeader[];
  onChange: (next: CustomHeader[]) => void;
}

export function CustomHeadersInput({ value, onChange }: Props) {
  const handleAdd = useCallback(() => {
    onChange([...value, emptyCustomHeader()]);
  }, [value, onChange]);

  const handleUpdate = useCallback(
    (idx: number, patch: Partial<CustomHeader>) => {
      const next = value.slice();
      next[idx] = { ...next[idx], ...patch };
      onChange(next);
    },
    [value, onChange],
  );

  const handleDelete = useCallback(
    (idx: number) => {
      onChange(value.filter((_, i) => i !== idx));
    },
    [value, onChange],
  );

  return (
    <div className="space-y-2" data-testid={domId("sec", "custom-headers")}>
      {value.length === 0 && (
        <p className="text-[11px] text-[#92989F]">
          No custom headers. Add one if your MCP server needs an API key or
          extra auth header.
        </p>
      )}

      {value.map((row, idx) => (
        <div
          key={row.id}
          className="flex items-center gap-2"
          data-testid={domId("sec", `custom-header-row-${idx}`)}
        >
          <input
            placeholder="Header name"
            value={row.name}
            onChange={(e) => handleUpdate(idx, { name: e.target.value })}
            data-testid={domId("txt", `custom-header-name-${idx}`)}
            className="w-36 rounded-lg border-[0.5px] border-[#DCDFE3] bg-white px-2.5 py-1.5 font-mono text-xs text-[#0B0809] placeholder:text-[#92989F] transition-colors duration-150 focus:border-[#713D98] focus:outline-none focus:ring-2 focus:ring-[#713D98]/10"
          />
          <input
            type="text"
            placeholder="Value"
            value={row.value}
            onChange={(e) => handleUpdate(idx, { value: e.target.value })}
            data-testid={domId("txt", `custom-header-value-${idx}`)}
            className="min-w-0 flex-1 rounded-lg border-[0.5px] border-[#DCDFE3] bg-white px-2.5 py-1.5 text-xs text-[#0B0809] placeholder:text-[#92989F] transition-colors duration-150 focus:border-[#713D98] focus:outline-none focus:ring-2 focus:ring-[#713D98]/10"
          />
          <button
            type="button"
            onClick={() => handleDelete(idx)}
            aria-label="Delete header"
            data-testid={domId("btn", `custom-header-delete-${idx}`)}
            className="flex h-7 w-7 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg border-[0.5px] border-[#DCDFE3] bg-white text-[#92989F] transition-colors duration-150 hover:border-[#fecaca] hover:bg-[#FEF2F2] hover:text-[#b91c1c]"
          >
            <X size={12} />
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={handleAdd}
        data-testid={domId("btn", "custom-header-add")}
        className="flex cursor-pointer items-center gap-1.5 rounded-lg border-[0.5px] border-dashed border-[#DCDFE3] px-3 py-1.5 text-xs text-[#92989F] transition-colors duration-150 hover:border-[#713D98] hover:text-[#713D98]"
      >
        + Add header
      </button>
    </div>
  );
}
